import { canExploreSchoolAccess } from "../features/help/SchoolAccessDialog";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import * as React from "react";
import { alpha, styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import {
  Box,
  ListItemIcon,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  AppBar,
  useMediaQuery,
  SwipeableDrawer,
  Container,
  Divider,
  useTheme,
  CssBaseline,
  ListItemText,
} from "@mui/material";
import {
  Logout,
  MenuOpen,
  HelpOutline,
  Sync,
  CorporateFare,
  PeopleAlt,
  QuizOutlined,
  CheckCircle,
  Spa,
  GroupWork,
  Groups,
  AccountTree,
} from "@mui/icons-material";
import Toolbar from "@mui/material/Toolbar";
import { useLocation, useNavigate } from "react-router-dom";
import FruitifyLogo from "./FruitifyLogo";
import { useAuthenticationStore } from "../stores/auth";
import FruitifyIcon from "./FruitifyIcons";
import { useState } from "react";
import { SwitchRoleDialog } from "./SwitchRoleDialog";
import GeneralLoading from "./Loading";
import { FruitifyContainer } from "./Containers";
import { checkHasApprovedSubscription } from "../features/auth/Utils";
import { useHelpStore } from "../stores/help";
import TieredFeatureAccess from "./TieredFeatureAccess";

const unsubscribedMainListItems = [
  { text: "Home", icon: <FruitifyIcon name={"Home"} />, link: "/" },
];

export const getSubscribedListItems = (
  activeContext = {},
  roles = [],
  onSwitchRole,
  hasApprovedSubscription = false,
  isIndependent = false,
  user,
) => {
  const roleBasedListItems = [];
  const { contextType, roleAssignment } = activeContext || {};

  if (contextType === "role_assignment") {
    if (roleAssignment.role.code === "student") {
      roleBasedListItems.push({
        text: "Assignments",
        icon: <FruitifyIcon name={"Assignment"} />,
        link: "/assignments",
      });
      roleBasedListItems.push({
        text: "Quizzes",
        icon: <QuizOutlined />,
        link: "/quizzes",
        disabled: isIndependent,
      });
    }
    if (roleAssignment.role.code === "teacher") {
      roleBasedListItems.push({
        text: "My Classes",
        icon: <FruitifyIcon name={"School"} />,
        link: "/school/classes",
      });
      roleBasedListItems.push({
        text: "Questions",
        icon: <HelpOutline />,
        link: "/questions",
      });
      roleBasedListItems.push({
        text: "Assignments",
        icon: <FruitifyIcon name={"Assignment"} />,
        link: "/assignments",
      });
      roleBasedListItems.push({
        text: "Quizzes",
        icon: <QuizOutlined />,
        link: "/quizzes",
        disabled: isIndependent,
      });
      roleBasedListItems.push({
        text: "Ask Bud",
        icon: <Spa />,
        link: "/assistant",
        disabled: isIndependent,
        showBetaChip: true,
      });
      roleBasedListItems.push({
        text: "Personalisation",
        icon: <FruitifyIcon name={"Tune"} />,
        link: "/personalisation/upload",
        disabled:
          !user.info.allowPersonalisation ||
          !hasApprovedSubscription ||
          isIndependent,
        upgradeRequired: !user.info.allowPersonalisation,
      });
    }

    if (roleAssignment.role.code === "director_of_learning") {
      const groupId = activeContext?.roleAssignment?.schoolGroup?.id;
      roleBasedListItems.push({
        text: "My Schools",
        icon: <FruitifyIcon name={"School"} />,
        link: `/schoolGroup/${groupId}/schools`,
      });
      roleBasedListItems.push({
        text: "Group Assessments",
        icon: <Groups />,
        link: "/assignments/groups",
      });
    }

    if (roleAssignment.role.code === "admin") {
      roleBasedListItems.push({
        text: "Classes",
        icon: <FruitifyIcon name={"School"} />,
        link: "/management/classes",
      });
      roleBasedListItems.push({
        text: "Teachers",
        icon: <FruitifyIcon name={"Teacher"} />,
        link: "/management/teachers",
      });
      roleBasedListItems.push({
        text: "Students",
        icon: <FruitifyIcon name={"Student"} />,
        link: "/management/students",
      });
      roleBasedListItems.push({
        text: "Assignments",
        icon: <FruitifyIcon name={"Assignment"} />,
        link: "/assignments/create",
      });
    }
  }
  if (contextType === "fruitify_moderator") {
    roleBasedListItems.push({
      text: "Moderate Answers",
      icon: <FruitifyIcon name={"Assignment"} />,
      link: "/moderator/dashboard",
    });
    roleBasedListItems.push({
      text: "Moderated History",
      icon: <CheckCircle />,
      link: "/moderator/history",
    });
  }

  if (roles.length > 1) {
    roleBasedListItems.push({
      text: "Switch role",
      icon: <FruitifyIcon name={"Switch"} />,
      onClick: onSwitchRole,
    });
  }

  if (contextType === "superuser") {
    roleBasedListItems.push({
      text: "Wonde Integration",
      icon: <Sync />,
      link: "/superuser/wonde",
    });
    roleBasedListItems.push({
      text: "Users",
      icon: <PeopleAlt />,
      link: "/superuser/users",
    });
    roleBasedListItems.push({
      text: "Schools",
      icon: <CorporateFare />,
      link: "/superuser/schools",
    });
    roleBasedListItems.push({
      text: "School Groups",
      icon: <GroupWork />,
      link: "/superuser/school_groups",
    });
    roleBasedListItems.push({
      text: "RAG",
      icon: <FruitifyIcon name={"Storage"} />,
      link: "/superuser/rag",
    });
    roleBasedListItems.push({
      text: "Marketing",
      icon: <AccountTree />,
      link: "/superuser/marketing",
    });
    roleBasedListItems.push({
      text: "API Access",
      icon: <FruitifyIcon name={"Key"} />,
      link: "/superuser/api-access",
    });
  }

  return [
    { text: "Home", icon: <FruitifyIcon name={"Home"} />, link: "/" },
    ...roleBasedListItems,
  ];
};

const secondaryListItems = [
  {
    text: "Account",
    icon: <FruitifyIcon name={"AccountCircle"} />,
    link: "/account/home",
  },
];

const drawerWidth = 280;
function MenuContent({ mainListItems, open, onOpenHelp, onOpenSchoolAccess }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthenticationStore();

  return (
    <Stack
      sx={{
        flexGrow: 1,
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {/* Main Menu */}
      <Stack
        spacing={2}
        sx={{ mt: 2, width: "70%" }}
        alignItems={open ? "flex-start" : "center"}
      >
        {mainListItems.map((item, i) => (
          <TieredFeatureAccess
            key={i}
            accessOverride={!item.upgradeRequired}
            menuItem
          >
            <MenuItem
              key={i}
              onClick={item.onClick ? item.onClick : () => navigate(item.link)}
              sx={{ minWidth: open && "100%" }}
              selected={
                item.link === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.link)
              }
              disabled={item.disabled}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {open && <ListItemText>{item.text}</ListItemText>}
            </MenuItem>
          </TieredFeatureAccess>
        ))}
      </Stack>

      {/* Secondary Menu */}
      <Stack
        spacing={2}
        alignItems={open ? "flex-start" : "center"}
        sx={{ mb: 2, width: "70%" }}
      >
        <Divider
          sx={{
            width: open ? "100%" : "50%",
            mx: open ? 0 : "auto",
          }}
        />

        <MenuItem
          aria-label="Help and tutorials"
          onClick={onOpenHelp || useHelpStore.getState().openHelp}
          sx={{ minWidth: open && "100%" }}
        >
          <ListItemIcon
            sx={{ minWidth: 40, display: "flex", justifyContent: "center" }}
          >
            <HelpOutline />
          </ListItemIcon>
          {open && (
            <ListItemText sx={{ whiteSpace: "normal" }}>
              Help and tutorials
            </ListItemText>
          )}
        </MenuItem>
        {canExploreSchoolAccess(user) && (
          <MenuItem
            aria-label="Explore school access"
            onClick={
              onOpenSchoolAccess || useHelpStore.getState().openSchoolAccess
            }
            sx={{ minWidth: open && "100%" }}
          >
            <ListItemIcon
              sx={{ minWidth: 40, display: "flex", justifyContent: "center" }}
            >
              <GroupsOutlined />
            </ListItemIcon>
            {open && (
              <ListItemText sx={{ whiteSpace: "normal" }}>
                Explore school access
              </ListItemText>
            )}
          </MenuItem>
        )}
        {user.info.subscriptionStatus !== "superuser" &&
          secondaryListItems.map((item, i) => (
            <MenuItem
              key={i}
              onClick={() => navigate(item.link)}
              sx={{ minWidth: open && "100%" }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {open && <ListItemText>{item.text}</ListItemText>}
            </MenuItem>
          ))}
        <MenuItem
          onClick={() => navigate("/auth/logout")}
          sx={{ minWidth: open && "100%" }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Logout />
          </ListItemIcon>
          {open && <ListItemText>Log Out</ListItemText>}
        </MenuItem>
      </Stack>
    </Stack>
  );
}

const LEFT_OFFSET = (theme) => theme.spacing(2); // uses theme spacing

const openedMixin = (theme) => ({
  width: drawerWidth, // e.g. 320
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  left: LEFT_OFFSET(theme),
  boxSizing: "border-box",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(10)} + 1px)`, // keep your collapsed width
  left: LEFT_OFFSET(theme),
  boxSizing: "border-box",

  "& .MuiButtonBase-root": {
    gap: 14,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    height: "96vh", // match hifi
    top: "2vh", // offset from top if needed
    overflow: "auto", // prevent extra scroll
    display: "flex",
    flexDirection: "column",
    "& .MuiSvgIcon-root": {
      width: "1.8rem",
      height: "1.8rem",
    },
    borderRight: "none",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.palette.baseShadow,
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

function MiniDrawer({ mainListItems }) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setHoverLogo(false);
  };

  const [hoverLogo, setHoverLogo] = useState(false);

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default }}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        open={open}
        data-testid={"fruitify-logged-in-drawer"}
      >
        <DrawerHeader
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            mt: 2,
          }}
        >
          {open ? (
            <Stack
              direction={"row"}
              sx={{ width: "80%" }}
              spacing={2}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <FruitifyLogo horizontal width={160} />
              <IconButton
                onClick={handleDrawerClose}
                sx={{
                  backgroundColor: "transparent",
                  border: 0,
                  "&:hover": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <FruitifyIcon
                  name="LayoutSidebar"
                  sx={{ stroke: "primary.main", fill: "transparent" }}
                />
              </IconButton>
            </Stack>
          ) : (
            <IconButton
              onClick={handleDrawerOpen}
              sx={{
                border: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                  boxShadow: "none",
                },
              }}
              onMouseEnter={() => setHoverLogo(true)}
              onMouseLeave={() => setHoverLogo(false)}
            >
              {hoverLogo ? (
                <FruitifyIcon
                  name="LayoutSidebar"
                  sx={{ stroke: "primary.main", fill: "transparent" }}
                />
              ) : (
                <FruitifyLogo />
              )}
            </IconButton>
          )}
        </DrawerHeader>
        <MenuContent mainListItems={mainListItems} open={open} />
      </Drawer>
    </Box>
  );
}

export default function SideMenu() {
  const isTablet = useMediaQuery("(max-width:960px)"); // Handles middle screen sizes
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const pendingHelp = React.useRef(null);
  const [switchRoleDialogOpen, setSwitchRoleDialogOpen] = React.useState(false);
  const { user } = useAuthenticationStore();
  const hasApprovedSubscription = checkHasApprovedSubscription();

  const onSwitchRole = () => {
    setSwitchRoleDialogOpen(true);
  };

  if (!user.info) {
    return (
      <FruitifyContainer>
        <GeneralLoading />
      </FruitifyContainer>
    );
  }

  const isIndependent = user?.info?.subscriptionStatus === "independent";

  const subscribedMainListItems = getSubscribedListItems(
    user.info.activeContext,
    user.info.roles,
    onSwitchRole,
    hasApprovedSubscription,
    isIndependent,
    user,
  );

  const isModerator =
    Boolean(user?.info?.moderator) ||
    user?.info?.roles?.some(
      (r) => String(r).toLowerCase() === "fruitify moderator",
    );

  const mainListItems =
    user?.info?.subscribed || isModerator
      ? subscribedMainListItems
      : unsubscribedMainListItems;

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  return (
    <>
      <SwitchRoleDialog
        open={switchRoleDialogOpen}
        setOpen={setSwitchRoleDialogOpen}
      />
      {/* Mobile & Tablet - Show AppBar with Menu Button */}
      {isTablet ? (
        <AppBar
          position="fixed"
          enableColorOnDark
          sx={{
            boxShadow: (theme) => `0 1px 0 ${theme.palette.divider}`,
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.96),
            backgroundImage: "none",
            backdropFilter: "blur(12px)",
            mt: "var(--template-frame-height, 0px)",
          }}
        >
          <Container maxWidth="xl">
            <Toolbar>
              <Stack
                direction={"row"}
                spacing={5}
                sx={{ width: "100%", display: { xs: "flex", md: "none" } }}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <IconButton
                  edge="start"
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                >
                  <MenuOpen />
                </IconButton>

                <FruitifyLogo horizontal sx={{ fontSize: 100 }} />
              </Stack>
            </Toolbar>
          </Container>
        </AppBar>
      ) : (
        // Desktop - Permanent Sidebar
        <MiniDrawer mainListItems={mainListItems} />
      )}

      {/* Mobile - Dropdown Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        {mainListItems.map((item, index) => (
          <TieredFeatureAccess
            accessOverride={item.upgradeRequired}
            key={index}
            menuItem
          >
            <MenuItem onClick={handleCloseMenu} disabled={item.disabled}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              {item.text}
            </MenuItem>
          </TieredFeatureAccess>
        ))}
      </Menu>

      {/* Tablet - Swipeable Drawer */}
      <SwipeableDrawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        SlideProps={{
          onExited: () => {
            if (pendingHelp.current) {
              const action = pendingHelp.current;
              pendingHelp.current = null;
              useHelpStore.getState()[action]();
            }
          },
        }}
      >
        <Box
          sx={{ width: drawerWidth }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <MenuContent
            mainListItems={mainListItems}
            open
            onOpenSchoolAccess={() => {
              pendingHelp.current = "openSchoolAccess";
              setDrawerOpen(false);
            }}
            onOpenHelp={() => {
              pendingHelp.current = "openHelp";
              setDrawerOpen(false);
            }}
          />
        </Box>
      </SwipeableDrawer>
    </>
  );
}
